import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACT, TAGLINE, SERVICES, NAV_LINKS } from "@/data/site";
import { FadeIn } from "@/components/Reveal";

export default function Footer() {
    return (
        <footer data-testid="main-footer" className="relative border-t border-line bg-deck">
            <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
                <div className="grid gap-12 lg:grid-cols-12">
                    <FadeIn className="lg:col-span-5">
                        <Logo />
                        <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-400">
                            {TAGLINE}
                        </p>
                        <p className="mt-6 font-display text-3xl font-extrabold uppercase leading-none text-white/5">
                            Lifting Systems
                            <br />
                            Engineered Right
                        </p>
                    </FadeIn>

                    <FadeIn delay={0.08} className="lg:col-span-2">
                        <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-accent">
                            Links
                        </h3>
                        <ul className="mt-5 space-y-3">
                            {NAV_LINKS.map((l) => (
                                <li key={l.path}>
                                    <Link
                                        to={l.path}
                                        data-testid={`footer-link-${l.testid.replace("nav-link-", "")}`}
                                        className="text-sm text-slate-400 transition-colors hover:text-accent"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </FadeIn>

                    <FadeIn delay={0.14} className="lg:col-span-2">
                        <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-accent">
                            Services
                        </h3>
                        <ul className="mt-5 space-y-3">
                            {SERVICES.map((s) => (
                                <li key={s.id}>
                                    <Link
                                        to="/services"
                                        data-testid={`footer-service-${s.id}`}
                                        className="text-sm text-slate-400 transition-colors hover:text-accent"
                                    >
                                        {s.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </FadeIn>

                    <FadeIn delay={0.2} className="lg:col-span-3">
                        <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-accent">
                            Contact
                        </h3>
                        <ul className="mt-5 space-y-4 text-sm text-slate-400">
                            {CONTACT.phones.map((p) => (
                                <li key={p.href}>
                                    <a
                                        href={p.href}
                                        data-testid={`footer-phone-${p.href.slice(6)}`}
                                        className="flex items-center gap-2.5 transition-colors hover:text-accent"
                                    >
                                        <Phone className="h-4 w-4 shrink-0 text-accent" />
                                        {p.display}
                                    </a>
                                </li>
                            ))}
                            {CONTACT.emails.map((e) => (
                                <li key={e.href}>
                                    <a
                                        href={e.href}
                                        data-testid={`footer-email-${e.display.split("@")[0]}`}
                                        className="flex items-start gap-2.5 break-all transition-colors hover:text-accent"
                                    >
                                        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                                        {e.display}
                                    </a>
                                </li>
                            ))}
                            <li className="flex items-start gap-2.5">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                                <span>{CONTACT.addressOneLine}</span>
                            </li>
                        </ul>
                        <Link
                            to="/contact"
                            data-testid="footer-enquire-link"
                            className="mt-6 inline-flex items-center gap-2 border border-accent px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-accent transition-colors hover:bg-accent hover:text-night"
                        >
                            Enquire Now
                            <ArrowUpRight className="h-3.5 w-3.5" />
                        </Link>
                    </FadeIn>
                </div>
            </div>
            <div className="border-t border-line/70">
                <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-2 px-4 py-6 sm:flex-row sm:px-6 lg:px-12">
                    <p className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-slate-500">
                        UMA ENGINEERING — Bangalore
                    </p>
                    <p className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-slate-500">
                        Engineering Solutions • Reliable Systems • Professional Service
                    </p>
                </div>
            </div>
        </footer>
    );
}
